import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { platformApi } from '../../api/platform';
import { useAuth } from '../../auth/AuthProvider';
import { EmptyState, ErrorBanner, LoadingState, PageHeader } from '../../components/Layout';

export default function ShopsPage() {
  const { getToken } = useAuth();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadShops = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await platformApi.listShops(token);
      setShops(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadShops();
  }, [loadShops]);

  const handleDelete = async (shop) => {
    if (!window.confirm(`Delete shop "${shop.name}" and drop schema ${shop.schema_name}?`)) {
      return;
    }
    setError(null);
    try {
      const token = await getToken();
      await platformApi.deleteShop(token, shop.id);
      await loadShops();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (shop) => {
    setError(null);
    try {
      const token = await getToken();
      await platformApi.updateShop(token, shop.id, { is_active: !shop.is_active });
      await loadShops();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  return (
    <>
      <PageHeader
        title="Shops"
        description="View shops created by merchants. Deactivate, delete, or assign shop admins from each shop's detail page."
      />

      <ErrorBanner message={error} />

      {shops.length === 0 ? (
        <EmptyState message="No shops yet. Merchants create stores when they sign up." />
      ) : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Schema</th>
                <th>Storefront</th>
                <th>Status</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shops.map((shop) => (
                <tr key={shop.id}>
                  <td>
                    <Link to={`/platform/shops/${shop.id}`}>{shop.name}</Link>
                  </td>
                  <td>{shop.slug}</td>
                  <td>
                    <span className="schema-pill">{shop.schema_name}</span>
                  </td>
                  <td>
                    {shop.storefront_url ? (
                      <a href={shop.storefront_url} target="_blank" rel="noreferrer">
                        {shop.storefront_hostname}
                      </a>
                    ) : (
                      shop.slug
                    )}
                  </td>
                  <td>
                    <span className={`badge ${shop.is_active ? 'badge-success' : 'badge-muted'}`}>
                      {shop.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(shop.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleToggleActive(shop)}
                      >
                        {shop.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(shop)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
